import React, { useState } from "react";

import ProfileFileUpload from "./ProfileFileUpload";
import { FormContainer, FullWidthContainer, InputField, Label, DeactivateTitle } from "../../../styles/PersonalInfoStep";
import { Tooltip } from "@mui/material";
import DatePickerFile from "../../../components/common/datepickerFile";
import dayjs from "dayjs";
import { 
  HeaderContainer, 
  StatusBadge, 
  ErrorMessage, 
  DocumentCard, 
  DocumentTooltip, 
  InfoTooltipIcon, 
  DocumentHeader, 
  DocumentLabel, 
  FormGroupWithMargin 
} from "../../../styles/profile";

type DocumentStatus = 'approved' | 'pending' | 'unapproved' | 'not-uploaded';

export interface DocumentData {
  file: File | null;
  expiryDate: Date | null;
  status: DocumentStatus;
}

export interface FormDataState {
  sfaLicense: DocumentData;
  halalLicense: DocumentData;
  liquorLicense: DocumentData;
  nricFront: DocumentData;
  nricBack: DocumentData;
  letterOfAuth: DocumentData;
  storeCover: DocumentData;
  storeCoverAR1: DocumentData;
}

// Mock data for testing with status
export const mockFormDataWithStatus: FormDataState = {
  sfaLicense: {
    file: new File(['dummy content'], 'sfa_license.pdf', { type: 'application/pdf' }),
    expiryDate: dayjs().add(1, 'year').toDate(),
    status: 'approved'
  },
  halalLicense: {
    file: new File(['dummy content'], 'halal_cert.jpg', { type: 'image/jpeg' }),
    expiryDate: dayjs().add(90, 'day').toDate(),
    status: 'pending'
  },
  liquorLicense: {
    file: new File(['dummy content'], 'liquor_license.pdf', { type: 'application/pdf' }),
    expiryDate: null,
    status: 'unapproved'
  },
  nricFront: {
    file: null,
    expiryDate: null,
    status: 'not-uploaded'
  },
  nricBack: {
    file: null,
    expiryDate: null,
    status: 'not-uploaded'
  },
  letterOfAuth: {
    file: null,
    expiryDate: null,
    status: 'not-uploaded'
  },
  storeCover: {
    file: new File(['dummy content'], 'store_cover.jpg', { type: 'image/jpeg' }),
    expiryDate: null,
    status: 'pending'
  },
  storeCoverAR1: {
    file: null,
    expiryDate: null,
    status: 'not-uploaded'
  }
};

// Test wrapper component for development
export const ProfileDocumentUploadTest = () => {
  const [formData, setFormData] = useState<FormDataState>(mockFormDataWithStatus);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleChange = (field: string, value: any) => {
    const key = field as keyof FormDataState;
    setFormData(prev => ({
      ...prev,
      [key]: { ...prev[key], ...value }
    }));

    // Simulate an upload error for a specific document for testing
    if (key === 'letterOfAuth' && value.file) {
      setTimeout(() => {
        setUploadError("Unable to upload. Please try again.");
      }, 2000);
    } else {
      setUploadError(null);
    }
  };

  return (
    <ProfileDocumentUpload 
      formData={formData}
      onChange={handleChange}
      uploadError={uploadError}
    />
  );
};

interface ProfileDocumentUploadProps {
  formData: FormDataState; 
  businessType?: string | null;
  onChange: (field: string, value: any) => void;
  uploadError?: string | null;
}

const ProfileDocumentUpload: React.FC<ProfileDocumentUploadProps> = ({
  formData,
  businessType,
  onChange,
  uploadError,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleFileChange = (docType: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      // Handle case where file selection is cancelled - reset to not-uploaded
      onChange(docType, { ...formData[docType as keyof FormDataState], file: null, status: 'not-uploaded' });
      setErrors(prev => ({ ...prev, [docType]: '' }));
      return;
    }

    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, [docType]: 'File format must be PDF, JPEG, or PNG.' }));
      onChange(docType, { ...formData[docType as keyof FormDataState], file: null, status: 'not-uploaded' }); // Clear invalid file and reset status
      e.target.value = ''; // Clear the input
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, [docType]: 'File size must be ≤ 5 MB.' }));
      onChange(docType, { ...formData[docType as keyof FormDataState], file: null, status: 'not-uploaded' }); // Clear invalid file and reset status
      e.target.value = ''; // Clear the input
      return;
    }

    setErrors(prev => ({ ...prev, [docType]: '' }));
    // When a new file is uploaded (either first time or replacement), set status to pending
    onChange(docType, { ...formData[docType as keyof FormDataState], file, status: 'pending' });
  };

  const handleDateChange = (docType: string, date: dayjs.Dayjs | null) => {
  
  if (date && date.isBefore(dayjs().startOf('day'))) {
    setErrors(prev => ({ ...prev, [`${docType}Expiry`]: 'Expiry date must be after today.' }));
    onChange(docType, { ...formData[docType as keyof FormDataState], expiryDate: null });
  } else {
    setErrors(prev => ({ ...prev, [`${docType}Expiry`]: '' }));
    const updateValue = { ...formData[docType as keyof FormDataState], expiryDate: date?.toDate() || null };
    onChange(docType, updateValue);
  }
};

const handleDelete = (docType: string) => {
  // When deleting a file, reset both file and status to not-uploaded, and clear expiry date
  onChange(docType, { 
    file: null, 
    status: 'not-uploaded' as DocumentStatus,
    expiryDate: null // Also clear expiry date when deleting
  });
  setErrors(prev => ({ ...prev, [docType]: '' }));
};

  const documentTypes = [
    {
      id: 'sfaLicense', // Changed from 'sfaLicensePath' to 'sfaLicense'
      label: 'SFA License',
      required: true,
      description: 'Singapore Food Agency license for food businesses',
      hasExpiry: false,
    },
    {
      id: 'halalLicense', // Changed from 'halalLicensePath' to 'halalLicense'
      label: 'Halal Certificate',
      required: false,
      description: 'Valid halal certification from MUIS or recognized body',
      hasExpiry: true,
    },
    {
      id: 'liquorLicense', // Changed from 'liquorLicensePath' to 'liquorLicense'
      label: 'Liquor License',
      required: false,
      description: 'Liquor License certification',
      hasExpiry: true,
    },
    {
      id: 'nricFront', // Changed from 'nricFrontPath' to 'nricFront'
      label: 'NRIC(Front)',
      required: true,
      description: 'Front side of Singapore NRIC or passport',
      hasExpiry: false,
    },
     {
      id: 'nricBack', 
      label: 'NRIC(Back)',
      required: true,
      description: 'Back side of Singapore NRIC or passport',
      hasExpiry: false,
    },
    {
      id: 'letterOfAuth', // Changed from 'letterOfAuthPath' to 'letterOfAuth'
      label: 'Biz File',
      required: businessType === "business"? true : false, // You may need to pass accountType as a separate prop if this needs to be conditional
      description: 'Biz File for business account representatives',
      hasExpiry: false,
    },
    {
      id: 'storeCover', // Changed from 'storeCoverPath' to 'storeCover'
      label: 'Store Cover (1:1)',
      required: true,
      description: 'Store Cover photo with 1:1 Aspect Ratio',
      hasExpiry: false,
    },
    {
      id: 'storeCoverAR1', // Changed from 'storeCoverAR1Path' to 'storeCoverAR1'
      label: 'Store Cover (16:9)',
      required: true,
      description: 'Store Cover photo with 16:9 Aspect Ratio',
      hasExpiry: false,
    },
  ];

  const documentTypesPersonal = [
    {
      id: 'sfaLicense', // Changed from 'sfaLicensePath' to 'sfaLicense'
      label: 'SFA License',
      required: true,
      description: 'Singapore Food Agency license for food businesses',
      hasExpiry: false,
    },
    {
      id: 'halalLicense', // Changed from 'halalLicensePath' to 'halalLicense'
      label: 'Halal Certificate',
      required: false,
      description: 'Valid halal certification from MUIS or recognized body',
      hasExpiry: true,
    },
    {
      id: 'liquorLicense', // Changed from 'liquorLicensePath' to 'liquorLicense'
      label: 'Liquor License',
      required: false,
      description: 'Liquor License certification',
      hasExpiry: true,
    },
    {
      id: 'nricFront', // Changed from 'nricFrontPath' to 'nricFront'
      label: 'NRIC(Front)',
      required: true,
      description: 'Front side of Singapore NRIC or passport',
      hasExpiry: false,
    },
     {
      id: 'nricBack', 
      label: 'NRIC(Back)',
      required: true,
      description: 'Back side of Singapore NRIC or passport',
      hasExpiry: false,
    },
    {
      id: 'storeCover', // Changed from 'storeCoverPath' to 'storeCover'
      label: 'Store Cover (1:1)',
      required: true,
      description: 'Store Cover photo with 1:1 Aspect Ratio',
      hasExpiry: false,
    },
    {
      id: 'storeCoverAR1', // Changed from 'storeCoverAR1Path' to 'storeCoverAR1'
      label: 'Store Cover (16:9)',
      required: true,
      description: 'Store Cover photo with 16:9 Aspect Ratio',
      hasExpiry: false,
    },
  ];

  return (
    <FullWidthContainer>
      <FormContainer $isProfile={true}>
        <HeaderContainer>
          <DeactivateTitle>Document Verification</DeactivateTitle>
          <DocumentTooltip 
            title="Upload or renew your expiring documents here. Approved documents keep your account Verified."
            arrow
            placement="left"
          >
            <InfoTooltipIcon />
          </DocumentTooltip>
        </HeaderContainer>

        {(businessType === "personal"? documentTypesPersonal : documentTypes).map((doc) => {
          const documentData = formData[doc.id as keyof FormDataState];
          return (
            <DocumentCard key={doc.id} elevation={0}>
              <DocumentHeader>
                <DocumentLabel>
                  {doc.label}
                  {doc.required ? <>*</> : <></>}
                </DocumentLabel>
                <StatusDisplay status={documentData?.status || 'not-uploaded'} />
              </DocumentHeader>

              <ProfileFileUpload
                inputId={`file-upload-${doc.id}`}
                label={doc.label}
                onChange={(e) => handleFileChange(doc.id, e)}
                files={documentData?.file ? [{ id: doc.id, imagePath: documentData.file }] : []}
                status={documentData?.status || 'not-uploaded'}
                // status="approved"
                onDelete={() => handleDelete(doc.id)}
                error={errors[doc.id]}
              />

              {doc.hasExpiry && documentData?.file && (
                <FormGroupWithMargin>
                  <FullWidthContainer>
                    <Label>{`${doc.label} Expired Date*`}</Label>
                    <InputField>
                      <DatePickerFile
                        value={documentData?.expiryDate ? dayjs(documentData.expiryDate) : null}
                        label=""
                        onChange={(date) => handleDateChange(doc.id, date)}
                        disablePast={true}
                        maxDate={dayjs().add(5, 'year')}
                        type="date"
                      />
                    </InputField>
                  </FullWidthContainer>
                </FormGroupWithMargin>
              )}

              {documentData?.status === 'unapproved' && (
                <ErrorMessage>
                  Your document was rejected. Please upload a valid document.
                </ErrorMessage>
              )}
              {uploadError && (
                <ErrorMessage>
                  {uploadError}
                </ErrorMessage>
              )}
            </DocumentCard>
          );
        })}
      </FormContainer>
    </FullWidthContainer>
  );
};

const StatusDisplay = ({ status }: { status: 'pending' | 'unapproved' | 'approved' | 'not-uploaded' }) => {
  const statusTextMap = {
    'pending': 'Pending Review',
    'unapproved': 'Unapproved',
    'approved': 'Approved',
    'not-uploaded': 'Not Uploaded',
  };

  const badge = <StatusBadge $status={status}>{statusTextMap[status]}</StatusBadge>;

  if (status === 'unapproved') {
    return (
      <Tooltip title="Please re-upload a valid document." arrow placement="top">
        {badge}
      </Tooltip>
    );
  }

  return badge;
};

export default ProfileDocumentUpload;